import { useState } from 'react';
import DatePicker from 'react-datepicker';
import Button from 'components/controls/buttons/button';
import CalendarIcon from 'assets/icons/calendar.svg';
import CloseX from 'assets/icons/close-x.svg';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './date-filter.module.scss';
import './datepicker.scss';
import { formatDate, newYorkizeDate } from 'helpers/date-time';
import { NYC_IANA_TIMEZONE } from 'lib/constants.mjs';

type Props = {
	startDate?: Date;
	endDate?: Date;
	minDate?: Date;
	formatWeekday?: any;
	monthsShown?: number;
	onCalendarChange: any;
	startOpen?: boolean;
};

const getDateText = (startDate, endDate) => {
	const dateFormat: Intl.DateTimeFormatOptions = {
		month: 'short',
		day: 'numeric',
		timeZone: NYC_IANA_TIMEZONE,
	};
	const from = startDate ? newYorkizeDate(startDate) : startDate;
	const to = endDate ? newYorkizeDate(endDate) : endDate;

	if (
		from &&
		to &&
		formatDate(from, dateFormat) != formatDate(to, dateFormat)
	) {
		return `${formatDate(from, dateFormat)}–${formatDate(to, dateFormat)}`;
	} else if (from) {
		return `${formatDate(from, dateFormat)}`;
	} else {
		return 'Select date';
	}
};

const resetButtonDisabled = (startDate, endDate) => {
	return startDate || endDate ? {} : { disabled: true };
};

const showPageFilter = (startDate, endDate) => {
	return startDate || endDate ? false : true;
};

const DateFilter = ({
	startDate,
	endDate,
	minDate,
	formatWeekday = (nameOfDay) => nameOfDay.substring(0, 1),
	monthsShown = 2,
	onCalendarChange,
	startOpen = false,
}: Props) => {
	const [pickerIsOpen, setPickerIsOpen] = useState(startOpen);

	const handlePickerToggle = () => {
		setPickerIsOpen(!pickerIsOpen);
	};

	const handleClearButton = () => {
		setPickerIsOpen(false);
		onCalendarChange([undefined, undefined]);
	};

	return (
		<div>
			{pickerIsOpen && (
				<div
					className={styles.datepickerBackdrop}
					onClick={() => setPickerIsOpen(false)}
				></div>
			)}
			<div className={styles.buttonContainer}>
				<button
					className={`${styles.button} ${!showPageFilter(startDate, endDate) || pickerIsOpen ? styles.buttonWithValue : ''}`}
					onClick={handlePickerToggle}
				>
					<span>{getDateText(startDate, endDate)}</span>
					<CalendarIcon />
				</button>
			</div>
			{pickerIsOpen && (
				<div className={styles.datepickerContainer}>
					<div className={styles.pickerClose}>
						<button
							className={styles.pickerCloseButton}
							onClick={() => setPickerIsOpen(false)}
						>
							Close
							<CloseX />
						</button>
					</div>
					<DatePicker
						selected={startDate}
						onChange={onCalendarChange}
						startDate={startDate}
						endDate={endDate}
						monthsShown={monthsShown}
						minDate={minDate}
						formatWeekDay={formatWeekday}
						selectsRange
						inline
					/>
					<div className={styles.datepickerButtonContainer}>
						<Button
							appearance="secondary"
							className={styles.resetButton}
							{...resetButtonDisabled(startDate, endDate)}
							onClick={handleClearButton}
						>
							Clear dates
						</Button>
						<Button appearance="primary" onClick={() => setPickerIsOpen(false)}>
							See results
						</Button>
					</div>
				</div>
			)}
		</div>
	);
};

export default DateFilter;
